import React, { useState } from "react";
import {
  Sprout, ArrowRight, Check, Sparkles, LineChart, Network, Boxes, Radio,
  Calculator, Bot, ShieldCheck, Upload, Wand2, Rocket, Star, Menu, X, ChevronDown,
  ShoppingCart, Store, Cpu, Smartphone, WashingMachine, Wheat, CloudRain, TrendingUp,
  Gauge, Layers, Brain, Droplets, Database, Mail, CreditCard, FileSpreadsheet, Plug,
} from "lucide-react";
import { ROICalculator } from "@/pages/ROICalculator";
import { useSEO } from "@/lib/useSEO";
import ContactSalesDialog from "@/shared/ContactSalesDialog";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/579fa117-e31d-4ed1-99ee-d96a70496ef5/images/e0606a74d1e2f2e8bdd8232bf473ae92636dc2e95e25b8a48b5cce25c305f032.png";
const IMG_AISLE = "https://images.unsplash.com/photo-1601599561096-f87c95fff1e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_MANAGER = "https://images.unsplash.com/photo-1641440615275-12344bd1276f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_PRODUCE = "https://images.unsplash.com/photo-1583258292688-d0213dc5a3a8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_FARM = "https://images.unsplash.com/photo-1779043151929-e57da43e83b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const IMG_FUTURES = "https://images.pexels.com/photos/38808473/pexels-photo-38808473.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const INDUSTRIES = [
  { icon: ShoppingCart, name: "Grocery & CPG", desc: "Category assortment across SKUs, produce and services.", demo: "grocery" },
  { icon: Store, name: "General Retail", desc: "Departments, classes and display space for any store.", demo: "retail", href: "/retail", linkLabel: "Learn more" },
  { icon: Cpu, name: "Electronics & Hardware", desc: "Model lineups, series and display-slot optimization.", demo: "electronics", href: "/electronics", linkLabel: "Learn more" },
  { icon: Smartphone, name: "Cellular & Mobile", desc: "Device lineups, accessories and plan attach.", demo: "cellular", href: "/cellular", linkLabel: "Learn more" },
  { icon: WashingMachine, name: "Appliances", desc: "Showroom-floor mix across refrigeration, laundry & more.", demo: "appliances", href: "/appliances", linkLabel: "Learn more" },
  { icon: Wheat, name: "Agriculture & Production", desc: "Production-mix optimization with live commodity futures.", demo: "agriculture", href: "/farmers", linkLabel: "Farmer tools" },
];

const AG_FEATURES = [
  { icon: TrendingUp, title: "Live commodity futures", body: "Real-time grain, livestock and dairy prices from market feeds so you plant, hold and sell against the actual curve — not last season's guess." },
  { icon: CloudRain, title: "Weather-driven yield signals", body: "Location-based forecasts translate into yield multipliers and revenue projections for every facility, updated continuously." },
  { icon: Droplets, title: "Soil moisture & drought watch", body: "Soil-moisture tracking and automatic drought alerts flag at-risk facilities before they hit your bottom line." },
  { icon: Bot, title: "AI market briefs & alerts", body: "A plain-English market brief plus custom price alerts tell you what moved, why it matters, and what to do next." },
];

const INTEGRATIONS = [
  { icon: Database, title: "Point of sale", body: "Stream live sales from any POS via our universal API, webhook or middleware — with turnkey connectors on higher tiers." },
  { icon: FileSpreadsheet, title: "CSV & Excel import", body: "Upload a sales/inventory export and AssortIQ auto-maps your columns and builds your dashboards in minutes." },
  { icon: TrendingUp, title: "Market & commodity feeds", body: "Live grain, livestock and dairy futures pulled from market data providers for the Agriculture vertical." },
  { icon: CloudRain, title: "Weather & soil signals", body: "Location-based forecasts and soil-moisture data drive yield multipliers and drought alerts per facility." },
  { icon: Mail, title: "Email alerts", body: "Price-trigger and market-brief alerts delivered straight to your team's inbox so signals reach people, not just pages." },
  { icon: CreditCard, title: "Payments & billing", body: "Secure subscription billing with self-serve plan upgrades — start on a free trial and scale when you're ready." },
];


const VALUES = [
  { icon: Wand2, title: "Decide what to keep, cut & expand", body: "AI scores every SKU and service on velocity, margin, turns and stockouts — then tells you exactly what to add, drop or grow, with the reasoning shown." },
  { icon: LineChart, title: "See demand before it happens", body: "Explainable statistical forecasts with confidence bands, promo and seasonality lift — so core products never run out and slow movers don't tie up cash." },
  { icon: Boxes, title: "Test changes before you commit", body: "A financial simulator and a per-store Digital Twin predict the revenue, margin and cash impact of any change before it hits a single shelf." },
];

const FEATURES = [
  { tag: "AI Assortment", icon: Sparkles, title: "The right products, in the right mix, at every store", body: "Category-specific optimization across SKUs and services. Get ranked keep / remove / expand recommendations with a plain-English rationale and the dollar impact of every move — including an “All Items” view across the whole store.", points: ["Keep / remove / expand scoring", "Dead-inventory & stockout detection", "Explainable AI rationale per SKU"], img: IMG_AISLE, flip: false },
  { tag: "Forecasting & Clustering", icon: Network, title: "Forecast demand and group stores that actually behave alike", body: "Statistical demand forecasting with 80/95% prediction intervals, plus AI store clustering that segments locations by real behavior — so regional grocers localize assortments instead of shipping one plan everywhere.", points: ["Demand forecasts with confidence bands", "Behavioral store clustering & maps", "Shelf & space optimization"], img: IMG_MANAGER, flip: true },
  { tag: "Digital Twin & Simulation", icon: Calculator, title: "Model the outcome before you roll it out", body: "Build a virtual twin of each store and simulate pricing, promotions, shelf, vendor and inventory changes. See projected revenue, gross profit, margin, cash flow, turns and ROI — with a clear GO / PILOT / NO-GO verdict.", points: ["Per-store Retail Digital Twin", "7-lever financial simulator", "Compare unlimited scenarios"], img: IMG_PRODUCE, flip: false },
];

const STEPS = [
  { icon: Upload, title: "Connect your data", body: "Import CSV/Excel or stream sales live from any POS. Your KPIs populate in minutes." },
  { icon: Bot, title: "Ask & optimize", body: "Your AI merchandising copilot answers plain-English questions and surfaces ranked opportunities." },
  { icon: Rocket, title: "Simulate & roll out", body: "Test the financial impact in a Digital Twin, then act with confidence — and measure the lift." },
];

const TESTIMONIALS = [
  { quote: "We cut slow-moving SKUs by a third and held sales flat — that's pure margin back in our pocket. The copilot pays for itself.", name: "Independent grocer, 3 stores", role: "Owner / Buyer" },
  { quote: "Being able to forecast by store and see weeks-of-supply changed how we buy. No more guessing on reorders.", name: "Regional chain", role: "Inventory Planning Manager" },
  { quote: "The Digital Twin let us pressure-test a price change across formats before rollout. We caught a margin hit early.", name: "Specialty market group", role: "Category Director" },
];

const FAQS = [
  { q: "What industries does AssortIQ support?", a: "One engine, six industries: Grocery & CPG, General Retail, Electronics & Hardware, Cellular & Mobile, Appliances, and Agriculture & Production. Each vertical re-skins the same optimization engine with its own terminology, categories and data — kept fully isolated per tenant — so the product speaks your language on day one." },
  { q: "How does it help farmers and producers?", a: "The Agriculture vertical adds live commodity futures (grains, livestock, dairy), weather-driven yield and soil-moisture signals, automatic drought watch, and AI market briefs — all feeding a production-mix optimizer that tells you what to grow, hold and sell against real market and weather data." },
  { q: "Do I need a data team to get started?", a: "No. Upload a CSV/Excel export or connect your POS and AssortIQ builds your dashboards, KPIs and AI recommendations automatically. Independents and single operators are up and running the same day." },
  { q: "How is the AI explainable?", a: "Every recommendation shows the factors behind it — velocity, margin, turns, stockouts, weeks-of-supply — and estimates the financial impact. Forecasts ship with 80/95% confidence bands. You always see the logic, not a black box." },
  { q: "Can it connect to my point-of-sale?", a: "Yes. A universal live-sales API lets any POS stream sales in real time via webhook or middleware, with turnkey connectors for popular systems on higher tiers." },
  { q: "What does it cost?", a: "Plans start at $997/year (introductory) for independents, scale to regional chains and multi-facility operations, and an Enterprise tier for large retailers. Every plan starts with a 7-day free trial." },
];

const NAV = [
  { label: "Industries", href: "#industries" },
  { label: "Features", href: "#features" },
  { label: "Integrations", href: "#integrations" },
  { label: "Pricing", href: "#pricing" },
  { label: "Free Tools", href: "/tools" },
  { label: "Resources", href: "/blog" },
];

export default function Landing({ onGetStarted, onLogin }) {
  const [menu, setMenu] = useState(false);
  const [faq, setFaq] = useState(0);
  const [demoOpen, setDemoOpen] = useState(false);
  const login = onLogin || onGetStarted;
  useSEO({
    title: "AssortIQ — AI Assortment Optimization for Grocery, Retail & Agriculture",
    description: "Stock what sells, eliminate what doesn't. Explainable-AI assortment optimization for independent & regional grocers, retailers and producers — a RELEX/NIQ alternative without the enterprise cost. Try a live demo, no signup.",
    canonical: "https://getassortiq.com/",
  });

  return (
    <div className="min-h-screen bg-forest-900 text-white font-body antialiased overflow-x-hidden">
      <ContactSalesDialog open={demoOpen} onOpenChange={setDemoOpen} source="landing-demo" title="Book a demo" />
      {/* NAV */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-forest-900/80 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-5 w-5" strokeWidth={2.4} /></div>
            <span className="font-heading font-extrabold text-xl">Assort<span className="text-ai">IQ</span></span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            {NAV.map((n) => <a key={n.href} href={n.href} className="hover:text-white transition-colors">{n.label}</a>)}
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button data-testid="nav-signin" onClick={login} className="text-sm font-semibold text-white/80 hover:text-white">Log in</button>
            <button data-testid="nav-cta" onClick={onGetStarted} className="group flex items-center gap-1.5 rounded-full bg-ai px-4 py-2 text-sm font-bold text-white hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
              Start free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
          <button className="md:hidden" onClick={() => setMenu((m) => !m)}>{menu ? <X /> : <Menu />}</button>
        </div>
        {menu && (
          <div className="md:hidden border-t border-white/10 px-6 py-4 space-y-3">
            {NAV.map((n) => <a key={n.href} href={n.href} onClick={() => setMenu(false)} className="block text-sm text-white/80">{n.label}</a>)}
            <button data-testid="mobile-login" onClick={login} className="w-full rounded-full border border-white/25 px-4 py-2.5 text-sm font-bold">Log in</button>
            <button onClick={onGetStarted} className="w-full rounded-full bg-ai px-4 py-2.5 text-sm font-bold">Start free trial</button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,94,0,0.18),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-ai/40 bg-ai/10 px-3 py-1 text-xs font-semibold text-ai uppercase tracking-wider">
              <Sparkles className="h-3.5 w-3.5" /> AI assortment optimization · 6 industries
            </div>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
              Stock what sells.<br /><span className="text-ai">Eliminate what doesn't.</span>
            </h1>
            <p className="mt-5 text-white/70 text-base sm:text-lg max-w-xl leading-relaxed">
              AssortIQ turns your sales, inventory and market data into category-specific decisions —
              what to add, remove and expand — with AI you can actually explain. Built for independents and
              regionals across <span className="text-white font-semibold">grocery, retail and agriculture</span>,
              including live commodity futures and weather signals for farmers.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button data-testid="hero-cta" onClick={onGetStarted} className="group flex items-center gap-2 rounded-full bg-ai px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
                Start 7-day free trial <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button data-testid="hero-login" onClick={login} className="rounded-full border border-white/25 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors">
                Log in
              </button>
              <a href="/demo/grocery" data-testid="hero-demo" className="rounded-full border border-white/25 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors">
                Try a live demo
              </a>
              <button type="button" data-testid="hero-book-demo" onClick={() => setDemoOpen(true)} className="rounded-full border border-white/25 px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors">
                Book a demo
              </button>
            </div>
            <p className="mt-4 text-xs text-white/50 flex items-center gap-2"><Check className="h-4 w-4 text-action-keep" /> No charge today · Cancel anytime · Set up in minutes</p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-ai/20 blur-3xl rounded-full" />
            <img src={HERO_IMG} alt="AssortIQ analytics dashboard" className="relative rounded-2xl border border-white/10 shadow-2xl w-full" />
          </div>
        </div>
      </section>

      {/* TRUST / METRICS */}
      <section className="border-y border-white/10 bg-white/[0.03]">
        <div className="mx-auto max-w-7xl px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[["6", "industries supported"], ["30%", "less dead inventory"], ["7 days", "free to try"], ["Real-time", "POS & market feeds"]].map(([n, l]) => (
            <div key={l}>
              <div className="font-heading text-3xl font-extrabold text-ai">{n}</div>
              <div className="text-sm text-white/60 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" className="mx-auto max-w-7xl px-6 py-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <Layers className="h-3.5 w-3.5" /> One engine, six industries
          </div>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold">Not just grocery — a multi-industry platform</h2>
          <p className="mt-3 text-white/60">The same explainable optimization engine re-skins itself for your industry — with the right terminology, categories and data, kept fully isolated per account. <span className="text-white/80 font-semibold">Try a live demo of any industry below — no signup needed.</span></p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {INDUSTRIES.map((ind) => (
            <div key={ind.name} data-testid={`industry-${ind.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-ai/40 hover:-translate-y-1 transition-all">
              <div className="h-11 w-11 rounded-xl bg-ai/15 text-ai flex items-center justify-center group-hover:bg-ai group-hover:text-white transition-colors"><ind.icon className="h-6 w-6" /></div>
              <h3 className="mt-4 font-heading text-lg font-bold">{ind.name}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{ind.desc}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                <a href={`/demo/${ind.demo}`} data-testid={`industry-demo-${ind.demo}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-ai hover:gap-2.5 transition-all">
                  See live demo <ArrowRight className="h-4 w-4" />
                </a>
                {ind.href && (
                  <a href={ind.href} data-testid={`industry-link-${ind.demo}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-white transition-colors">
                    {ind.linkLabel || "Learn more"}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section id="features" className="mx-auto max-w-7xl px-6 py-20 border-t border-white/10">
        <div className="max-w-2xl">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">Better buying, backed by AI</h2>
          <p className="mt-3 text-white/60">Everything a category manager needs to build winning assortments — without a data science team.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {VALUES.map((v) => (
            <div key={v.title} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-ai/40 hover:-translate-y-1 transition-all">
              <div className="h-11 w-11 rounded-xl bg-ai/15 text-ai flex items-center justify-center"><v.icon className="h-6 w-6" /></div>
              <h3 className="mt-4 font-heading text-lg font-bold">{v.title}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURE SECTIONS */}
      <section className="bg-white/[0.02] border-y border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-8 divide-y divide-white/10">
          {FEATURES.map((f) => (
            <div key={f.title} className="py-14 grid lg:grid-cols-2 gap-10 items-center">
              <div className={f.flip ? "lg:order-2" : ""}>
                <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">
                  <f.icon className="h-3.5 w-3.5" /> {f.tag}
                </div>
                <h3 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold leading-tight">{f.title}</h3>
                <p className="mt-3 text-white/65 leading-relaxed">{f.body}</p>
                <ul className="mt-5 space-y-2.5">
                  {f.points.map((p) => (
                    <li key={p} className="flex items-center gap-2.5 text-sm text-white/80">
                      <span className="h-5 w-5 rounded-full bg-action-keep/15 text-action-keep flex items-center justify-center shrink-0"><Check className="h-3 w-3" /></span>{p}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`relative ${f.flip ? "lg:order-1" : ""}`}>
                <div className="absolute -inset-3 bg-ai/10 blur-2xl rounded-full" />
                <img src={f.img} alt={f.title} className="relative rounded-2xl border border-white/10 shadow-xl w-full h-72 object-cover" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* AGRICULTURE / FARMERS */}
      <section id="agriculture" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,94,0,0.12),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-3 bg-ai/10 blur-2xl rounded-full" />
              <img src={IMG_FARM} alt="Farmer inspecting a golden wheat field" className="relative rounded-2xl border border-white/10 shadow-xl w-full h-80 object-cover" />
              <div className="relative mt-4 rounded-2xl border border-white/10 overflow-hidden hidden sm:block">
                <img src={IMG_FUTURES} alt="Live commodity futures charts" className="w-full h-40 object-cover opacity-90" />
              </div>
            </div>
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <Wheat className="h-3.5 w-3.5" /> For farmers & producers
              </div>
              <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">Optimize your production mix against real markets & weather</h2>
              <p className="mt-3 text-white/65 leading-relaxed">AssortIQ's Agriculture vertical goes beyond assortment: it plugs live commodity futures and location-based weather straight into your grow, hold and sell decisions — the kind of market intelligence enterprise assortment tools simply don't offer producers.</p>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                {AG_FEATURES.map((a) => (
                  <div key={a.title} data-testid={`ag-feature-${a.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-ai/40 transition-colors">
                    <a.icon className="h-6 w-6 text-ai" />
                    <h3 className="mt-3 font-heading text-base font-bold">{a.title}</h3>
                    <p className="mt-1.5 text-sm text-white/60 leading-relaxed">{a.body}</p>
                  </div>
                ))}
              </div>
              <a href="/farmers" data-testid="farmers-page-link" className="mt-8 inline-flex items-center gap-2 rounded-full bg-ai px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
                See the farmer platform <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* METHODOLOGY / EXPLAINABLE AI */}
      <section id="methodology" className="bg-white/[0.02] border-y border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">
              <Brain className="h-3.5 w-3.5" /> Explainable by design
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold">No black box — see the math behind every call</h2>
            <p className="mt-3 text-white/60">Category managers act on recommendations they can defend. AssortIQ scores every item on the signals you already trust, then shows its work.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Gauge, t: "Velocity & turns", d: "Units sold, sell-through rate and inventory turns per item and category." },
              { icon: TrendingUp, t: "Margin & impact", d: "Gross margin and the estimated dollar impact of every keep / cut / expand move." },
              { icon: Boxes, t: "Stock health", d: "Stockouts, dead inventory and weeks-of-supply flagged automatically." },
              { icon: LineChart, t: "Confidence bands", d: "Forecasts ship with 80/95% prediction intervals — not single-point guesses." },
            ].map((m) => (
              <div key={m.t} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <div className="h-11 w-11 rounded-xl bg-ai/15 text-ai flex items-center justify-center"><m.icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-heading text-base font-bold">{m.t}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="mx-auto max-w-7xl px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">Live in three steps</h2>
          <p className="mt-3 text-white/60">From raw data to confident decisions — fast enough for a single-store owner, powerful enough for a chain.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <div className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-ai text-white font-heading font-bold flex items-center justify-center text-sm">{i + 1}</div>
              <s.icon className="h-7 w-7 text-ai" />
              <h3 className="mt-3 font-heading text-lg font-bold">{s.title}</h3>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white/[0.02] border-y border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-center">Built for how grocers actually buy</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 flex flex-col">
                <div className="flex gap-1 text-ai mb-3">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-ai" />)}</div>
                <p className="text-sm text-white/80 leading-relaxed flex-1">"{t.quote}"</p>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-white/50">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-white/40">Illustrative outcomes based on typical assortment optimization results.</p>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section id="integrations" className="bg-white/[0.02] border-y border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">
              <Plug className="h-3.5 w-3.5" /> Integrations
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold">Connects to the data you already have</h2>
            <p className="mt-3 text-white/60">No rip-and-replace. AssortIQ plugs into your sales, market and operational data so recommendations reflect what's really happening.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {INTEGRATIONS.map((it) => (
              <div key={it.title} data-testid={`integration-${it.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-ai/40 transition-colors">
                <div className="h-11 w-11 rounded-xl bg-ai/15 text-ai flex items-center justify-center"><it.icon className="h-6 w-6" /></div>
                <h3 className="mt-4 font-heading text-lg font-bold">{it.title}</h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR */}
      <ROICalculator onGetStarted={onGetStarted} />

      {/* PRICING TEASER */}
      <section id="pricing" className="mx-auto max-w-7xl px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">Pricing you grow into</h2>
          <p className="mt-3 text-white/60">Start free, scale as you add stores or facilities. Every plan includes a 7-day free trial — no charge today.</p>
        </div>
        <p className="mt-3 text-center text-xs text-white/45">All six industries included · Prices in USD, billed annually · Cancel anytime</p>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { name: "Grow", price: "$997", retail: "$1,995", sub: "Independent grocers · up to 5 stores", feat: ["AI assortment recommendations", "Dashboards & KPIs", "Dead-inventory detection", "AI assistant"], hot: false },
            { name: "Scale", price: "$4,997", retail: "$9,995", sub: "Regional chains · up to 50 stores", feat: ["Everything in Grow", "Forecasting & clustering", "Shelf & financial simulator", "Live POS + API access"], hot: true },
            { name: "Optimize", price: "Custom", retail: "", sub: "Large retailers · unlimited", feat: ["Everything in Scale", "Retail Digital Twin", "SSO & ERP/POS integrations", "White-label & dedicated CSM"], hot: false },
          ].map((p) => (
            <div key={p.name} className={`relative rounded-2xl p-6 flex flex-col ${p.hot ? "bg-white text-forest-800 ring-2 ring-ai" : "border border-white/10 bg-white/[0.03]"}`}>
              {p.hot && <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-ai px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">Most popular</div>}
              <div className="font-heading text-xl font-extrabold">{p.name}</div>
              <div className={`text-xs mt-0.5 ${p.hot ? "text-forest-500" : "text-white/50"}`}>{p.sub}</div>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-heading text-3xl font-extrabold">{p.price}</span>
                {p.price !== "Custom" && <span className={`text-sm mb-1 ${p.hot ? "text-forest-500" : "text-white/50"}`}>/yr</span>}
              </div>
              {p.retail && <div className={`text-xs ${p.hot ? "text-forest-500" : "text-white/50"}`}><span className="line-through">{p.retail}</span> · 50% introductory</div>}
              <ul className="mt-5 space-y-2 flex-1">
                {p.feat.map((x) => <li key={x} className={`flex items-start gap-2 text-sm ${p.hot ? "text-forest-600" : "text-white/70"}`}><Check className="h-4 w-4 text-action-keep mt-0.5 shrink-0" />{x}</li>)}
              </ul>
              <button onClick={onGetStarted} className={`mt-6 w-full rounded-full py-2.5 text-sm font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 ${p.hot ? "bg-ai text-white hover:bg-[#e05400]" : "border border-white/25 hover:bg-white/10"}`}>
                {p.price === "Custom" ? "Contact sales" : "Start free trial"}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white/[0.02] border-y border-white/10">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-center">Frequently asked questions</h2>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <div key={f.q} className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden">
                <button data-testid={`faq-${i}`} onClick={() => setFaq(faq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                  <span className="font-semibold text-sm">{f.q}</span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-ai transition-transform ${faq === i ? "rotate-180" : ""}`} />
                </button>
                {faq === i && <p className="px-5 pb-4 text-sm text-white/65 leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <div className="relative overflow-hidden rounded-3xl border border-ai/30 bg-gradient-to-br from-ai/20 to-transparent p-10 sm:p-14 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,94,0,0.15),transparent_70%)]" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">Ready to optimize your assortment?</h2>
            <p className="mt-3 text-white/70 max-w-xl mx-auto">See what to keep, cut and grow in your first session. Start your free trial — no charge today.</p>
            <button data-testid="footer-cta" onClick={onGetStarted} className="mt-7 inline-flex items-center gap-2 rounded-full bg-ai px-7 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5">
              Start 7-day free trial <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-ai flex items-center justify-center"><Sprout className="h-4 w-4" strokeWidth={2.4} /></div>
            <span className="font-heading font-extrabold">Assort<span className="text-ai">IQ</span></span>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/50"><ShieldCheck className="h-4 w-4" /> Enterprise-grade security · Multi-tenant · Role-based access</div>
          <div className="text-xs text-white/40">© {new Date().getFullYear()} AssortIQ. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
